// Customer Dashboard widget — referral program: share, track, earn
import { useEffect, useState } from "react";
import { Gift, Copy, Check, Users, Mail, ExternalLink, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

export default function ReferralCard() {
  const { t, lang } = useLang();
  const ct = t.referral;

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/referrals/me");
        setData(r.data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[ReferralCard] /referrals/me failed (likely not logged in):", err?.response?.status);
        }
      }
    })();
  }, []);

  if (!data) return null;

  const message =
    lang === "ar" ? data.share_message_ar : lang === "en" ? data.share_message_en : data.share_message_de;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[ReferralCard] clipboard copy failed:", err);
      }
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(ct.email_subject)}&body=${encodeURIComponent(message)}`;

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-[#090A0B] via-[#1A1B1F] to-[#090A0B] text-white p-7 sm:p-9 border border-neutral-800 shadow-2xl"
      data-testid="referral-card"
    >
      {/* Decorative orange glow */}
      <div className="absolute -top-32 -end-32 w-64 h-64 bg-[#FF5A00] rounded-full opacity-20 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FF5A00]/20 text-[#FF5A00]">
            <Gift size={18} />
          </div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00]">
            {ct.eyebrow}
          </p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tighter leading-tight mb-2">
          {ct.title.replace("{eur}", Math.round(data.reward_per_referral_eur))}
        </h2>
        <p className="text-sm text-neutral-400 max-w-xl leading-relaxed mb-6">
          {ct.subtitle}
        </p>

        {/* Code box */}
        <div className="bg-black/40 border border-neutral-700 p-4 sm:p-5 mb-6">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral-500 mb-2">
            {ct.your_code}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="font-black text-3xl tracking-widest text-[#FF5A00] flex-1">
              {data.my_code}
            </div>
            <button
              type="button"
              onClick={() => copy(data.my_code, "code")}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white px-4 py-2 text-xs font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 transition"
              data-testid="referral-copy-code"
            >
              {copied === "code" ? (
                <>
                  <Check size={14} /> {ct.copied}
                </>
              ) : (
                <>
                  <Copy size={14} /> {ct.copy_code}
                </>
              )}
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-3 break-all font-mono">{data.share_url}</p>
        </div>

        {/* Share buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366] hover:bg-[#1EBE5C] text-white px-4 py-3 text-xs font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 transition"
            data-testid="referral-share-whatsapp"
          >
            <MessageCircle size={14} /> {ct.share_whatsapp}
          </a>
          <a
            href={emailUrl}
            className="bg-white text-[#090A0B] hover:bg-neutral-200 px-4 py-3 text-xs font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 transition"
            data-testid="referral-share-email"
          >
            <Mail size={14} /> {ct.share_email}
          </a>
          <button
            type="button"
            onClick={() => copy(message, "msg")}
            className="border border-neutral-700 hover:bg-neutral-800 text-white px-4 py-3 text-xs font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 transition"
            data-testid="referral-share-copy"
          >
            {copied === "msg" ? (
              <>
                <Check size={14} /> {ct.copied}
              </>
            ) : (
              <>
                <Copy size={14} /> {ct.share_copy}
              </>
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 pt-5 border-t border-neutral-800">
          <Stat value={data.referrals_count} label={ct.stat_invites} />
          <Stat value={data.redeemed_count} label={ct.stat_redeemed} accent />
          <Stat value={`${data.total_earned_eur.toFixed(0)} €`} label={ct.stat_earned} accent />
        </div>

        <p className="text-[10px] text-neutral-500 mt-5 leading-relaxed">
          <Users size={11} className="inline-block me-1 -mt-0.5" />
          {ct.footnote.replace("{eur}", Math.round(data.reward_per_referral_eur))}
        </p>
      </div>
    </div>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-black tracking-tighter tabular-nums ${accent ? "text-[#FF5A00]" : "text-white"}`}>
        {value}
      </div>
      <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}
